/**
 * @description The agents the chat can talk to, plus the path and storage helpers derived from them.
 *
 * Agents are **configuration rows in the agents database**, not code — the service builds its registry
 * from the DB at startup. So the record key here is a wire value we send in the URL path
 * (`/api/agents/{name}/…`), which is what the service switches on; there is no picker and no
 * negotiation. The only way to get it wrong is a typo, which is why each name is spelled once here
 * rather than at each call site.
 *
 * One agent is active today. The plumbing is nevertheless agent-parameterised — the API path takes an
 * agent, and stored sessions are keyed by agent name inside their value — so adding a second agent is
 * a registry entry plus a call site choosing a name, with no storage migration. See
 * `useChatSession.ts` for why the agent lives in the value rather than in the `localStorage` key.
 */

const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

export interface ChatAgentDescriptor {
  /** How long a session survives without a message. Per-agent so one can differ later. */
  sessionTtlMs: number;
}

export const CHAT_AGENTS = {
  'one-bestie': { sessionTtlMs: THREE_HOURS_MS },
} as const satisfies Record<string, ChatAgentDescriptor>;

export type ChatAgentName = keyof typeof CHAT_AGENTS;

/** The agent every chat call targets. Same value in every environment. */
export const CHAT_AGENT: ChatAgentName = 'one-bestie';

/** Shared prefix for every chat-session value, so the sign-out sweep can enumerate them. */
export const CHAT_SESSION_KEY_PREFIX = 'chat-session:';

export const isChatAgentName = (value: string | null | undefined): value is ChatAgentName => (
  !!value && Object.prototype.hasOwnProperty.call(CHAT_AGENTS, value)
);

/**
 * @description Agent-scoped proxy path.
 *
 * Never hand-build `/api/agent/...` (singular): that legacy route bakes `bestie` into its upstream URL
 * (`app/api/agent/[...path]/route.ts`), so it cannot reach `one-bestie` at all. That is why the
 * agent-scoped route is required rather than merely tidier.
 */
export const agentApiPath = (path: string, agent: ChatAgentName = CHAT_AGENT) => (
  `/api/agents/${agent}/${path}`
);

/**
 * @description Which placeholder the chat input shows.
 *
 * Audience-specific rather than agent-specific: asking someone posting a job about their career plans
 * reads as nonsense, and that stays true however many agents there are. Both keys already exist in
 * every locale.
 *
 * `undefined` (header info not loaded yet) falls back to the candidate copy — the larger audience, and
 * being wrong now costs only wording.
 */
export const chatPlaceholderKey = (isEmployer: boolean | undefined) => (
  isEmployer ? 'offer.page.hire.box.input.placeholder' : 'homepage.chat.input.placeholder'
);

/**
 * @description `localStorage` key for a user's chat sessions.
 *
 * Scoped by userId so an account switch on a shared device cannot inherit a thread. Deliberately **not**
 * scoped by agent: one record per user holds a map of `agent -> session`, so renaming or replacing an
 * agent never changes the key format and needs no migration. See `parseStoredSessions`.
 */
export const sessionKey = (userId: string) => `${CHAT_SESSION_KEY_PREFIX}${userId}`;

/**
 * @description Remove every stored chat session.
 *
 * Called on sign-out. Sweeps the whole prefix rather than one user's key: by the time a session-expiry
 * sign-out is noticed the userId is already gone, so there is nothing to scope by — and leaving a record
 * behind would let the next visitor on a shared device resume a thread that is not theirs.
 *
 * Reads the key list before deleting: `localStorage.key(i)` re-indexes as entries are removed, so
 * mutating while iterating skips keys.
 */
export const clearChatSessionStorage = (): void => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(CHAT_SESSION_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Storage unavailable (Safari private mode). Nothing was persisted, so nothing to clear.
  }
};
